import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { LOSS_REASONS, LOSS_REASON_LABELS, PAYROLL_LOSS_KEY, type LossReason } from "@torre/expenses";
import { useTheme } from "../../../context/ThemeContext";
import { apiErrorMessage, fetchLosses, type LossesResponse } from "../services/expensesService";
import { money, monthLabel } from "../lib/format";
import { ui } from "../theme";
import { Kpi, Notice, Spinner } from "./ui";

// ---------------------------------------------------------------------------
// Pérdidas (#1177): dinero que salió y no se puede justificar.
//
//   • Nómina: las horas pagadas (la jornada que cubre el sueldo) contra las
//     registradas en Horas Trabajadas. Justificadas + no justificadas = pagadas;
//     lo no justificado, a su costo por hora, es la pérdida.
//   • Otras: los gastos marcados como pérdida con su motivo.
//
// Quien no lleva seguimiento de horas no es pérdida: se muestra aparte. Si no
// se pudo cruzar con Horas Trabajadas, se dice — «no pude cruzar» no es «no
// hubo pérdida».
// ---------------------------------------------------------------------------

export const LOSS_COLORS: Record<string, string> = {
  [PAYROLL_LOSS_KEY]: "#dc2626",
  sin_uso: "#f97316",
  cobro_indebido: "#d946ef",
  multa_recargo: "#eab308",
  fraude: "#7f1d1d",
  tipo_de_cambio: "#0ea5e9",
  otro: "#6b7280",
};
const HOUR_COLORS = { justified: "#059669", unjustified: "#dc2626", untracked: "#9ca3af" };

const hoursText = (n: number) => `${Math.round(n * 10) / 10} h`;

export default function LossesCard({ from, to, reload = 0, onData }: { from: string; to: string; reload?: number; onData?: (d: LossesResponse | null) => void }) {
  const { t, i18n } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const lang = i18n?.language || "es";
  const [data, setData] = useState<LossesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    fetchLosses({ from, to })
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
        onData?.(d);
      })
      .catch((err) => {
        if (!alive) return;
        setError(apiErrorMessage(err) || t("expenses.losses.loadError"));
        onData?.(null);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, reload]);

  const reasonLabel = (key: string) =>
    key === PAYROLL_LOSS_KEY ? t("expenses.losses.payrollSeries") : LOSS_REASON_LABELS[key as LossReason]?.[lang.startsWith("en") ? "en" : "es"] ?? key;

  const lossChart = useMemo(
    () =>
      (data?.months || []).map((m) => ({
        label: monthLabel(m.period, lang),
        [PAYROLL_LOSS_KEY]: m.payroll,
        ...Object.fromEntries(LOSS_REASONS.map((r) => [r, m.byReason[r] || 0])),
      })),
    [data, lang],
  );
  const usedSeries = useMemo(
    () => [PAYROLL_LOSS_KEY, ...LOSS_REASONS].filter((k) => lossChart.some((row) => Number((row as Record<string, unknown>)[k]) > 0)),
    [lossChart],
  );
  const hoursChart = useMemo(
    () =>
      (data?.payrollHours?.months || []).map((m) => ({
        label: `${monthLabel(m.period, lang)}${m.inProgress ? " *" : ""}`,
        justified: m.justifiedHours,
        unjustified: m.unjustifiedHours,
        untracked: m.untrackedHours,
      })),
    [data, lang],
  );

  const axis = { fontSize: 11, fill: isDarkMode ? "#9ca3af" : "#6b7280" };
  const grid = isDarkMode ? "#333" : "#e5e7eb";
  const ph = data?.payrollHours;
  const totals = ph?.totals;
  const share = totals && totals.paidHours > 0 ? Math.round((totals.unjustifiedHours / totals.paidHours) * 100) : 0;

  return (
    <div className={c.card} data-testid="losses-card">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className={`text-sm font-semibold ${c.text}`}>
            <i className="ri-error-warning-line mr-1 text-red-500" />
            {t("expenses.losses.title")}
          </h3>
          <p className={`text-xs ${c.muted}`}>{t("expenses.losses.subtitle")}</p>
        </div>
      </div>

      {error && <Notice tone="warn">{error}</Notice>}
      {!data && !error && <Spinner />}
      {data && (
        <>
          <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label={t("expenses.losses.total")} value={money(data.total, "MXN", lang)} tone={data.total > 0 ? "bad" : "good"} />
            <Kpi
              label={t("expenses.losses.payroll")}
              value={money(data.payroll, "MXN", lang)}
              hint={totals ? t("expenses.losses.payrollHint", { hours: hoursText(totals.unjustifiedHours), share }) : undefined}
              tone={data.payroll > 0 ? "bad" : undefined}
            />
            <Kpi label={t("expenses.losses.other")} value={money(data.other, "MXN", lang)} hint={t("expenses.losses.otherHint", { count: data.count })} tone={data.other > 0 ? "bad" : undefined} />
            {totals && (
              <Kpi
                label={t("expenses.losses.hoursPaid")}
                value={hoursText(totals.paidHours)}
                hint={t("expenses.losses.hoursSplit", { justified: hoursText(totals.justifiedHours), unjustified: hoursText(totals.unjustifiedHours) })}
              />
            )}
          </div>

          {data.hoursError && <Notice tone="warn">{t("expenses.losses.hoursError")}</Notice>}
          {ph && (ph.beforeTracking?.length ?? 0) > 0 && (
            <Notice tone="info">
              {ph.trackingFrom
                ? t("expenses.losses.trackingFrom", { month: monthLabel(ph.trackingFrom, lang), count: ph.beforeTracking?.length ?? 0 })
                : t("expenses.losses.noTrackingYet")}
            </Notice>
          )}
          {ph && ph.uncovered.length > 0 && (
            <Notice tone="warn">{t("expenses.losses.uncovered", { months: ph.uncovered.map((p) => monthLabel(p, lang)).join(", ") })}</Notice>
          )}
          {data.withoutRate > 0 && <Notice tone="warn">{t("expenses.losses.withoutRate", { count: data.withoutRate })}</Notice>}
          {/* Personas y montos solo viajan a los dueños; los demás ven las horas. */}
          {totals && totals.untrackedHours > 0 && (
            <p className={`mb-3 text-xs ${c.muted}`} data-testid="losses-untracked">
              {totals.untrackedPeople != null && totals.untrackedBase != null
                ? t("expenses.losses.untrackedNote", { count: totals.untrackedPeople, hours: hoursText(totals.untrackedHours), amount: money(totals.untrackedBase, "MXN", lang) })
                : t("expenses.losses.untrackedHoursNote", { hours: hoursText(totals.untrackedHours) })}
            </p>
          )}

          {/* La nómina en horas: justificadas + no justificadas = pagadas. */}
          {totals && totals.paidHours > 0 && (
            <div className="mb-4" data-testid="losses-hours-bar">
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div style={{ width: `${100 - share}%`, background: HOUR_COLORS.justified }} title={t("expenses.losses.justified")} />
                <div style={{ width: `${share}%`, background: HOUR_COLORS.unjustified }} title={t("expenses.losses.unjustified")} />
              </div>
              <div className={`mt-1 flex flex-wrap justify-between gap-2 text-xs ${c.muted}`}>
                <span>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: HOUR_COLORS.justified }} />
                  {t("expenses.losses.justified")}: <b className={c.text}>{hoursText(totals.justifiedHours)}</b>
                  {totals.justifiedBase != null && <> · {money(totals.justifiedBase, "MXN", lang)}</>}
                </span>
                <span>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: HOUR_COLORS.unjustified }} />
                  {t("expenses.losses.unjustified")}: <b className="text-red-500">{hoursText(totals.unjustifiedHours)}</b> · {money(totals.lossBase, "MXN", lang)}
                </span>
                <span>
                  = {t("expenses.losses.hoursPaid")}: <b className={c.text}>{hoursText(totals.paidHours)}</b>
                  {totals.justifiedBase != null && <> · {money(totals.justifiedBase + totals.lossBase, "MXN", lang)}</>}
                </span>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div>
              <h4 className={`mb-1 text-xs font-semibold ${c.muted}`}>{t("expenses.losses.chartMoney")}</h4>
              {usedSeries.length === 0 ? (
                <p className={`py-10 text-center text-sm ${c.muted}`}>{t("expenses.losses.empty")}</p>
              ) : (
                <div className="h-56" data-testid="losses-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={lossChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                      <XAxis dataKey="label" tick={axis} />
                      <YAxis tick={axis} tickFormatter={(v) => `$${Math.round(Number(v) / 1000)}k`} width={48} />
                      <Tooltip formatter={(v: number, name: string) => [money(v, "MXN", lang), reasonLabel(name)]} contentStyle={{ fontSize: 12 }} />
                      <Legend formatter={(v: string) => reasonLabel(v)} wrapperStyle={{ fontSize: 11 }} />
                      {usedSeries.map((k) => (
                        <Bar key={k} dataKey={k} stackId="loss" fill={LOSS_COLORS[k]} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            {ph && (
              <div>
                <h4 className={`mb-1 text-xs font-semibold ${c.muted}`}>{t("expenses.losses.chartHours")}</h4>
                <div className="h-56" data-testid="losses-hours-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hoursChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                      <XAxis dataKey="label" tick={axis} />
                      <YAxis tick={axis} tickFormatter={(v) => `${v} h`} width={52} />
                      <Tooltip formatter={(v: number, name: string) => [hoursText(v), t(`expenses.losses.${name}`)]} contentStyle={{ fontSize: 12 }} />
                      <Legend formatter={(v: string) => t(`expenses.losses.${v}`)} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="justified" stackId="h" fill={HOUR_COLORS.justified} />
                      <Bar dataKey="unjustified" stackId="h" fill={HOUR_COLORS.unjustified} />
                      <Bar dataKey="untracked" stackId="h" fill={HOUR_COLORS.untracked} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                {ph.months.some((m) => m.inProgress) && <p className={`mt-1 text-xs ${c.muted}`}>{t("expenses.losses.inProgress")}</p>}
              </div>
            )}
          </div>

          {(ph?.byPerson?.length || data.expenses.length > 0) && (
            <button type="button" className={`${c.secondary} mt-3`} onClick={() => setOpen((o) => !o)} data-testid="losses-detail-toggle">
              <i className={`mr-1 ${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`} />
              {t(open ? "expenses.losses.hideDetail" : "expenses.losses.showDetail")}
            </button>
          )}

          {open && ph?.byPerson && ph.byPerson.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs" data-testid="losses-people">
                <thead>
                  <tr className={`text-left ${c.muted}`}>
                    <th className="p-2">{t("expenses.losses.person")}</th>
                    <th className="p-2 text-right">{t("expenses.losses.hoursPaid")}</th>
                    <th className="p-2 text-right">{t("expenses.losses.justified")}</th>
                    <th className="p-2 text-right">{t("expenses.losses.unjustified")}</th>
                    <th className="p-2 text-right">{t("expenses.losses.extra")}</th>
                    <th className="p-2 text-right">{t("expenses.losses.lossAmount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {ph.byPerson.map((p) => (
                    <tr key={p.teamMemberId} className={`border-t ${isDarkMode ? "border-dark-border" : "border-gray-100"} ${c.text}`}>
                      <td className="p-2">
                        {p.name}
                        {!p.tracked && <span className={`ml-2 rounded bg-gray-400/15 px-1.5 py-0.5 text-[10px] ${c.muted}`}>{t("expenses.losses.untrackedTag")}</span>}
                      </td>
                      <td className="p-2 text-right">{hoursText(p.paidHours)}</td>
                      <td className="p-2 text-right">{p.tracked ? hoursText(p.justifiedHours) : "—"}</td>
                      <td className={`p-2 text-right ${p.unjustifiedHours > 0 ? "font-semibold text-red-500" : ""}`}>{p.tracked ? hoursText(p.unjustifiedHours) : "—"}</td>
                      <td className="p-2 text-right">{p.extraHours > 0 ? hoursText(p.extraHours) : "—"}</td>
                      <td className={`p-2 text-right ${p.lossCost > 0 ? "font-semibold text-red-500" : ""}`}>
                        {p.tracked ? money(p.lossCost, p.currency, lang) : "—"}
                        {p.tracked && p.currency !== "MXN" && p.lossBase != null && <span className={`block text-[10px] font-normal ${c.muted}`}>{money(p.lossBase, "MXN", lang)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {open && data.expenses.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs" data-testid="losses-expenses">
                <thead>
                  <tr className={`text-left ${c.muted}`}>
                    <th className="p-2">{t("expenses.fields.date")}</th>
                    <th className="p-2">{t("expenses.fields.vendor")}</th>
                    <th className="p-2">{t("expenses.losses.reason")}</th>
                    <th className="p-2 text-right">{t("expenses.losses.lossAmount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.expenses.map((e) => (
                    <tr key={e.id} className={`border-t ${isDarkMode ? "border-dark-border" : "border-gray-100"} ${c.text}`}>
                      <td className="p-2 whitespace-nowrap">{e.date}</td>
                      <td className="p-2">
                        {e.vendor}
                        {e.lossNote && <span className={`block text-[10px] ${c.muted}`}>{e.lossNote}</span>}
                      </td>
                      <td className="p-2">
                        <span className="mr-1 inline-block h-2 w-2 rounded-full" style={{ background: LOSS_COLORS[e.lossReason] || LOSS_COLORS.otro }} />
                        {reasonLabel(e.lossReason)}
                      </td>
                      <td className="p-2 text-right font-semibold text-red-500">{e.amountBase == null ? "—" : money(e.amountBase, "MXN", lang)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
